import { useRef, useEffect, useState, useCallback, useMemo, useLayoutEffect } from 'react';
import { debounce as lodashDebounce } from 'lodash';

interface UseScrollBottomProps {
  offset?: number;
  debounce?: number;
  ref?: any;
}

const useScrollBottom = ({ offset = 0, debounce = 100, ref }: UseScrollBottomProps = {}): [
  boolean,
  React.Ref<HTMLElement>
] => {
  const [isBottom, setIsBottom] = useState(false);
  const [elementVersion, setElementVersion] = useState(0);
  const scrollRef = useRef<HTMLElement>(null);
  const innerScrollRef = ref || scrollRef;
  const prevElementRef = useRef<HTMLElement | null>(null);

  const checkScroll = useCallback(
    (element: HTMLElement | Window) => {
      if (element === window) {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = window.innerHeight;
        return scrollTop >= scrollHeight - clientHeight - offset;
      } else {
        const { scrollTop, scrollHeight, clientHeight } = element as HTMLElement;
        return scrollTop >= scrollHeight - clientHeight - offset;
      }
    },
    [offset]
  );

  useLayoutEffect(() => {
    const localRef = innerScrollRef.current;
    if (localRef !== prevElementRef.current) {
      setIsBottom(false);
      prevElementRef.current = localRef;
      setElementVersion((v) => v + 1);
    }
  });

  const onScroll = useCallback(
    (event: Event) => {
      let scrolledElement: HTMLElement | Window;
      let elementForScrollCheck: HTMLElement | Window;

      if (event.target === document || event.target === window || event.target === document.documentElement) {
        scrolledElement = window;
        elementForScrollCheck = (document.scrollingElement as HTMLElement) || document.documentElement;
      } else {
        scrolledElement = (event.target as HTMLElement) || (event.currentTarget as HTMLElement);
        elementForScrollCheck = scrolledElement as HTMLElement;
      }

      if (innerScrollRef.current) {
        const element = innerScrollRef.current;

        if (scrolledElement === element) {
          const tieneScroll = elementForScrollCheck.scrollHeight > elementForScrollCheck.clientHeight;
          if (tieneScroll) {
            setIsBottom(checkScroll(element));
          }
        } else if (scrolledElement === window) {
          const tieneScroll = elementForScrollCheck.scrollHeight > elementForScrollCheck.clientHeight;
          const refTieneScroll = element.scrollHeight > element.clientHeight;
          if (!refTieneScroll) {
            if (tieneScroll) {
              setIsBottom(checkScroll(window));
            }
          }
        }
      } else {
        if (scrolledElement === window) {
          const tieneScroll = elementForScrollCheck.scrollHeight > elementForScrollCheck.clientHeight;
          if (tieneScroll) {
            setIsBottom(checkScroll(window));
          }
        }
      }
    },
    [checkScroll, innerScrollRef]
  );

  const debouncedOnScroll = useMemo(() => lodashDebounce(onScroll, debounce), [onScroll, debounce]);

  useEffect(() => {
    return () => {
      debouncedOnScroll.cancel();
    };
  }, [debouncedOnScroll]);

  useEffect(() => {
    const localRef = innerScrollRef.current;

    window.addEventListener('scroll', debouncedOnScroll);

    if (localRef) {
      localRef.addEventListener('scroll', debouncedOnScroll);
    }

    return () => {
      window.removeEventListener('scroll', debouncedOnScroll);
      if (localRef) {
        localRef.removeEventListener('scroll', debouncedOnScroll);
      }
    };
  }, [debouncedOnScroll, elementVersion, innerScrollRef]);

  return [isBottom, innerScrollRef];
};

export default useScrollBottom;
